import React from 'react';
import { Button, Tooltip, Row } from 'antd';
import PropTypes from 'prop-types';
import {
  Map as CustomMap,
  Marker,
  FeatureGroup,
  Polygon,
  ZoomControl,
} from 'react-leaflet';
import Control from 'react-leaflet-control';
import L from 'leaflet';
import { GoogleLayer } from 'react-leaflet-google-v2';
import RefreshIcon from 'mdi-react/RefreshIcon';
import TrashCanIcon from 'mdi-react/TrashCanIcon';
import CloseIcon from 'mdi-react/CloseIcon';
import MapMarkerIcon from 'mdi-react/MapMarkerIcon';
import { MAP_API_KEY, MAP_CHANNEL_VERSION } from '../../../utils/constants';
import { objectIsEmpty } from '../../../utils/helpers';
import GreenMarkerIcon from '../../sharedComponents/GreenMarkerIcon/GreenMarkerIcon';
import RectangleTag from '../../sharedComponents/RectangleTag/RectangleTag';
import './ImportShapefiles.scss';

const shapeCaptions = [
  {
    color: 'rgba(250, 100, 0, 0.65)',
    text: 'Já cadastrados',

    borderInfo: {
      borderStyle: 'solid',
      borderColor: '#ff6600',
      borderWidth: '2px',
    },
  },
  {
    color: 'rgba(36, 101, 74, 0.6)',
    text: 'Importados',

    borderInfo: {
      borderStyle: 'dotted',
      borderColor: '#bbff33',
      borderWidth: '2px',
    },
  },
  { color: 'rgba(216, 216, 216, 10)', text: 'Removidos' },
];

const GeoMapShapefiles = (props): JSX.Element => {
  const {
    stage,
    zoom,
    mapCenter,
    land,
    clickedShape,
    shapes,
    handleStateField,
  } = props;

  const totalShapesRemoved = (): number => {
    let count = 0;

    shapes.forEach((element) => {
      if (element.isDeleted) {
        count += 1;
      }
    });

    return count;
  };

  const totalShapesImports = (): number => {
    let count = 0;

    shapes.forEach((element) => {
      if (!element.isDeleted && element.origin === 'shapefile') {
        count += 1;
      }
    });

    return count;
  };

  const handleDeletedShape = (clickedShapeP): void => {
    const clickedShapeIndex = shapes.indexOf(clickedShapeP);

    shapes[clickedShapeIndex].isDeleted = !clickedShapeP.isDeleted;

    handleStateField('shapes', shapes);
    handleStateField('forceRerender', 1);
  };

  const closeControls = (clickedShapeP): void => {
    shapes.forEach((element, index) => {
      if (element.key === clickedShapeP.key) {
        shapes[index].isSelected = false;
      }
    });

    handleStateField('shapes', shapes);
    handleStateField('clickedShape', {});
  };

  const clickPolygon = (item, index): void => {
    shapes.forEach((element, index2) => {
      if (element.key !== item.key) {
        shapes[index2].isSelected = false;
      }
    });

    if (item.origin === 'shapefile') {
      shapes[index].isSelected = !item.isSelected;
    }

    handleStateField('shapes', shapes);
    handleStateField('clickedShape', item.isSelected ? item : {});
  };

  const renderPolygons = (shapesP): any => {
    let borderColor: string;
    let borderOpacity: number;
    let polygonColor: string;
    let polygonOpacity: number;
    let dashArray: string;

    return shapesP.map((item, index) => {
      const text = L.divIcon({ className: 'polygon-text', html: item.key });

      if (item.origin === 'shapeland') {
        dashArray = '0';
        borderColor = '#ff6600';
        borderOpacity = 1.0;
        polygonColor = 'rgba(250, 100, 0, 0.65)';
        polygonOpacity = 0.7;
      } else if (item.isDeleted && !item.isSelected) {
        dashArray = '0';
        borderColor = 'rgba(0, 0, 0, 0.45)';
        borderOpacity = 0;
        polygonColor = 'rgba(216, 216, 216, 0.6)';
        polygonOpacity = 1.0;
      } else if (!item.isDeleted && item.isSelected) {
        dashArray = '0';
        borderColor = '#f5222d';
        borderOpacity = 1.0;
        polygonColor = 'rgba(36, 101, 74, 0.6)';
        polygonOpacity = 1.0;
      } else if (item.isDeleted && item.isSelected) {
        dashArray = '0';
        borderColor = '#f5222d';
        borderOpacity = 1.0;
        polygonColor = 'rgba(216, 216, 216, 0.6)';
        polygonOpacity = 1.0;
      } else if (!item.isDeleted) {
        dashArray = '5,15';
        borderColor = '#bbff33';
        borderOpacity = 1.0;
        polygonColor = 'rgba(36, 101, 74, 0.6)';
        polygonOpacity = 1.0;
      }

      return (
        <Polygon
          onClick={
            (): void => {
              clickPolygon(item, index);
            }
          }
          key={item.key}
          _leaflet_id={item.key}
          positions={item.polygon}
          color={borderColor}
          // weight={3} espessura das bordas - default: 3px
          opacity={borderOpacity}
          fillColor={polygonColor}
          lineJoin="miter"
          lineCap="square"
          dashArray={dashArray}
          fillOpacity={polygonOpacity}
        >
          {item.origin === 'shapefile' && <Marker position={item.polygonCenter} icon={text} />}
        </Polygon>
      );
    });
  };

  return (
    <div className="ImportShapefiles">
      <CustomMap
        className="map"
        id="map-google"
        center={mapCenter}
        zoomControl={false}
        zoom={zoom}
      >
        <ZoomControl position="topright" />

        <GoogleLayer
          googlekey={MAP_API_KEY}
          maptype="HYBRID"
          version={MAP_CHANNEL_VERSION}
        />

        <FeatureGroup>
          {renderPolygons(shapes)}
        </FeatureGroup>

        <GreenMarkerIcon
          position={land.location}
          popupText="Propriedade"
        />

        {
          !objectIsEmpty(clickedShape) && stage === 2 && (
            <Control position="topright">
              <Tooltip placement="left" title="Cancelar">
                <Button
                  onClick={
                    (): void => {
                      closeControls(clickedShape);
                    }
                  }
                  style={{ fontSize: '1em', width: '32px', padding: '4px 0' }}
                >
                  <CloseIcon style={{ padding: '0', fontSize: '1em' }} />
                </Button>
              </Tooltip>
            </Control>
          )
        }

        {
          !objectIsEmpty(clickedShape) && clickedShape.isDeleted && stage === 2 && (
            <Control position="topright">
              <Tooltip placement="left" title="Habilitar">
                <Button
                  onClick={
                    (): void => {
                      handleDeletedShape(clickedShape);
                    }
                  }
                  style={{ fontSize: '1em', width: '32px', padding: '4px 0' }}
                >
                  <RefreshIcon style={{ padding: '0', fontSize: '1em' }} />
                </Button>
              </Tooltip>
            </Control>
          )
        }

        {
          !objectIsEmpty(clickedShape) && !clickedShape.isDeleted && stage === 2 && (
            <Control position="topright">
              <Tooltip placement="left" title="Remover">
                <Button
                  onClick={
                    (): void => {
                      handleDeletedShape(clickedShape);
                    }
                  }
                  style={{ fontSize: '1em', width: '32px', padding: '4px 0' }}
                >
                  <TrashCanIcon style={{ padding: '0', fontSize: '1em' }} />
                </Button>
              </Tooltip>
            </Control>
          )
        }

        <Control position="bottomleft">
          <div className="shape-counter">
            <span>{`${totalShapesImports()} talhões serão importados`}</span>
          </div>
        </Control>

        <Control position="bottomleft">
          <div className="shape-counter">
            <span>{`${totalShapesRemoved()} talhões removidos`}</span>
          </div>
        </Control>

        <Control className="captions" position="bottomright">
          {
            shapeCaptions.map((element) => (
              <Row key={element.text}>
                <div className="shape-captions">
                  <RectangleTag
                    color={element.color}
                    size="18px"
                    margins={{ marginRight: '5px', marginTop: '10px' }}
                    borderInfo={element.borderInfo}
                  />
                  <span style={{ marginTop: '10px' }}>{element.text}</span>
                </div>
              </Row>
            ))
          }

          <Row>
            <div className="shape-captions">
              <MapMarkerIcon color="#a0c665" className="land-icon" />
              <span>Propriedade</span>
            </div>
          </Row>
        </Control>
      </CustomMap>
    </div>
  );
};

GeoMapShapefiles.propTypes = {
  stage: PropTypes.number.isRequired,
  zoom: PropTypes.number.isRequired,
  mapCenter: PropTypes.arrayOf(PropTypes.number).isRequired,
  clickedShape: PropTypes.shape().isRequired,
  handleStateField: PropTypes.func.isRequired,
  shapes: PropTypes.arrayOf(PropTypes.any).isRequired,

  land: PropTypes.shape({
    location: PropTypes.arrayOf(PropTypes.number).isRequired,
  }).isRequired,
};

GeoMapShapefiles.defaultProps = {};

export default GeoMapShapefiles;
